import React, { useEffect } from "react"
import { Communication, useUpdateCommunication } from "../../api/communications"
import { useFileUpload } from "../hooks/useFileUpload"
import { Upload, Trash } from "lucide-react"
import { Button } from "@/ui/Button"

interface FinancialStatementTabProps {
  communicationId: string
  year?: number
  quarter?: number
  communication?: Communication
}

const FinancialStatementTab: React.FC<FinancialStatementTabProps> = ({
  communicationId,
  year,
  quarter,
  communication,
}) => {
  const { isLoading: isUpdating, mutateAsync: updateCommunication } =
    useUpdateCommunication()

  const financialStatementDoc = React.useMemo(() => {
    if (!communication?.documents) return null
    return communication.documents.find(
      (doc) => doc.document_type === "financial_statement",
    )
  }, [communication?.documents])

  const initialFileName = financialStatementDoc?.filename || null

  const {
    uploading,
    uploadedFile,
    error,
    handleFileChange,
    FileInput,
    triggerUpload,
    setUploadedFile,
  } = useFileUpload({
    acceptedTypes: [".pdf"],
    maxSizeBytes: 50 * 1024 * 1024, // 50MB limit
    initialFileName: initialFileName,
    onUpload: async (signedId: string, file: File) => {
      await updateCommunication({
        id: communicationId,
        financial_statement_signed_id: signedId,
      })
    },
    onError: (error) => {
      console.error("Upload failed:", error)
      alert(`Failed to upload file: ${error.message}`)
    },
  })

  const handleRemoveDocument = async () => {
    if (!financialStatementDoc) return

    if (confirm("Are you sure you want to remove this document?")) {
      try {
        await updateCommunication({
          id: communicationId,
          delete_financial_statement: true,
        })
      } catch (error) {
        console.error("Failed to remove document:", error)
        alert("Failed to remove document. Please try again.")
      }
    }
  }

  useEffect(() => {
    if (!financialStatementDoc && uploadedFile) {
      setUploadedFile(null)
    }
  }, [financialStatementDoc, uploadedFile, setUploadedFile])

  const isLoading = !communication

  const displayFileName = uploadedFile || financialStatementDoc?.filename
  const hasUploadedFile = !!(uploadedFile || financialStatementDoc)
  const buttonText =
    uploading || isUpdating
      ? uploading
        ? "Uploading..."
        : "Updating..."
      : hasUploadedFile
      ? "Replace"
      : "Upload"

  return (
    <div className="financial-statement-tab">
      <h2 className="tab-heading">Financial Statement</h2>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <FileInput id="financial-statement-upload" />

      {isLoading ? (
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading document information...</p>
        </div>
      ) : (
        <div className="data-table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Document</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  {financialStatementDoc?.file_url ? (
                    <a
                      href={financialStatementDoc.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {`Q${quarter || ""} ${
                        year || ""
                      } QPCSP Financial Statement`}
                    </a>
                  ) : (
                    `Q${quarter || ""} ${year || ""} QPCSP Financial Statement`
                  )}
                </td>
                <td>
                  <div
                    className="table-actions"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      gap: "8px",
                    }}
                  >
                    {!hasUploadedFile && !uploading && (
                      <span className="no-file-text">no file chosen</span>
                    )}

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const input = document.getElementById(
                          "financial-statement-upload",
                        ) as HTMLInputElement
                        input?.click()
                      }}
                      disabled={uploading || isUpdating}
                      loading={uploading || isUpdating}
                      loadingText={buttonText}
                      icon={
                        !uploading && !isUpdating ? (
                          <Upload size={18} />
                        ) : undefined
                      }
                    >
                      {buttonText}
                    </Button>

                    {financialStatementDoc && !isUpdating && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={handleRemoveDocument}
                        icon={<Trash size={18} />}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default FinancialStatementTab
